import { action, computed, observable } from 'mobx';
import { LoyaltyApiSdk } from '~api/LoyaltyApiSdk';
import { FormatUserAuthData } from '~utils/FormatUserAuthData';
import {
    ILoyaltyUserAuth,
    IUserAuthStates,
    IFormattedAuthResult
} from '~api/interfaces/ILoyaltyUserAuth';
import { LoyaltyFieldString } from '~api/interfaces/ILoyaltyApiResponse';
import { IJWTApiResponse } from '~api/interfaces/IJWTToken';

enum Status {
    IDLE = 'IDLE',
    LOADING = 'LOADING',
    ERROR = 'ERROR',
    LOADED = 'LOADED',
    UNAUTHORIZED = 'UNAUTHORIZED'
}

export const authStates: IUserAuthStates = {
    SIGNEDIN: 'SIGNEDIN',
    ANONYMOUS: 'ANONYMOUS',
    RECOGNIZED: 'RECOGNIZED'
};

export class AuthenticatedRepository {
    private initialPromise: Promise<void>;
    private lylapiSdk: LoyaltyApiSdk;
    @observable private JWTToken: LoyaltyFieldString = null;
    @observable private JWTAPIInitiated: boolean = false;
    @observable private APIError: boolean = false;
    @observable private status: Status = Status.IDLE;
    @observable public authState: string = authStates.ANONYMOUS;
    @observable public isSignedIn: boolean = false;
    @observable public loyaltyActiveFlag: boolean = false;
    @observable public JWTAPIEnabled: boolean = true;

    constructor(loyaltyApiSdk: LoyaltyApiSdk) {
        this.lylapiSdk = loyaltyApiSdk;
    }

    @action public ensureData() {
        if (this.isUnfullfilled) {
            this.initialPromise = this.loadData();
        }

        return this.initialPromise;
    }

    @action public resetStatus() {
        this.status = Status.IDLE;
    }

    @action private async loadData() {
        try {
            this.status = Status.LOADING;
            !this.APIError && this.JWTAPIEnabled && !this.JWTAPIInitiated && await this.fetchJWT();
            if (!this.APIError && (!this.JWTAPIEnabled || this.JWTAPIEnabled && this.JWTToken !== null)) {
                await this.fetchUserAuthState();
                if (this.authState === authStates.SIGNEDIN) {
                    await this.fetchData();
                    this.status = Status.LOADED;
                }

            }
        } catch (err) {
            this.APIError = true;
            this.status = Status.ERROR;
        }
    }

    @action public async fetchData() {}

    @action public async fetchJWTAndUserAuthData() {

        this.status = Status.LOADING;
        !this.APIError && this.JWTAPIEnabled && !this.JWTAPIInitiated && await this.fetchJWT();
        !this.APIError && this.JWTToken && await this.fetchUserAuthState();

    }

    @action public async fetchJWT() {
        try {
            this.JWTAPIInitiated = true;
            const JWTApiResponse: IJWTApiResponse = await this.lylapiSdk.getJWTToken();
            this.setJWTToken(JWTApiResponse);
        } catch (err) {
            this.APIError = true;
            this.status = Status.UNAUTHORIZED;
        }
    }
    @action public setJWTToken = (JWTApiResponse: IJWTApiResponse) => {
        this.JWTToken = JWTApiResponse?.data?.token || null;
        this.lylapiSdk.setJWTToken(this.JWTAPIEnabled, this.JWTToken);
    };
    @computed public get getJWTToken() {
       return this.JWTToken;
    }

    @action public async fetchUserAuthState() {
        try {
            const userAuthData: ILoyaltyUserAuth = await this.lylapiSdk.getUserAuthState();
            const { authState, hasLoyalty } = FormatUserAuthData(
                userAuthData,
                authStates as IUserAuthStates
            ) as IFormattedAuthResult;
            this.authState = authState;
            this.loyaltyActiveFlag = hasLoyalty;
            this.isSignedIn =  userAuthData?.user?.signedIn || false;
        } catch (err) {
            this.APIError = true;
            this.status = Status.ERROR;
            this.isSignedIn = false;
        }
    }

    @computed public get isUnfullfilled() {
        return [Status.IDLE, Status.ERROR].includes(this.status);
    }
}
